<template>
  <div class="notification-bell" ref="bellRef">
    <!-- Icône de cloche avec badge -->
    <button 
      class="bell-btn"
      @click="toggleDropdown"
      :class="{ 'has-notifications': unreadCount > 0 }"
    >
      <font-awesome-icon icon="bell" class="bell-icon" />
      <span v-if="unreadCount > 0" class="badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
    </button>

    <!-- Dropdown -->
    <transition name="slide-down">
      <div v-if="isOpen" class="notifications-dropdown">
        <!-- ✅ HEADER AVEC BOUTON DE FERMETURE -->
        <div class="dropdown-header">
          <span class="title">
            <font-awesome-icon icon="bell" />
            Notifications
          </span>
          <div class="header-actions">
            <button 
              v-if="unreadCount > 0"
              class="mark-all-read"
              @click="markAllAsRead"
            >
              Tout marquer comme lu
            </button>
            <button 
              class="close-dropdown"
              @click="closeDropdown"
              title="Fermer"
            >
              <font-awesome-icon icon="times" />
            </button>
          </div>
        </div>

        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
        </div>

        <div v-else-if="notifications.length === 0" class="empty-state">
          <font-awesome-icon icon="bell-slash" />
          <p>Aucune notification</p>
        </div>

        <div v-else class="notifications-list">
          <div 
            v-for="notif in notifications" 
            :key="notif.id"
            class="notification-item"
            :class="{ unread: !notif.is_read }"
            @click="handleNotificationClick(notif)"
          >
            <div class="notif-icon" :style="{ background: notif.color }">
              <font-awesome-icon :icon="notif.icon" />
            </div>
            <div class="notif-content">
              <div class="notif-header">
                <span class="notif-title">{{ notif.title }}</span>
                <span class="notif-time">{{ notif.time_ago }}</span>
              </div>
              <p class="notif-message">{{ notif.message }}</p>
            </div>
            <button 
              v-if="!notif.is_read"
              class="notif-mark-read"
              @click.stop="markAsRead(notif)"
              title="Marquer comme lu"
            >
              <font-awesome-icon icon="check" />
            </button>
          </div>
        </div>

        <div class="dropdown-footer">
          <router-link to="/notifications" @click="closeDropdown">
            Voir toutes les notifications
          </router-link>
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onUnmounted } from 'vue';
import { $api } from '@/main';

interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  link: string | null;
  is_read: boolean;
  created_at: string;
  time_ago: string;
  icon: string;
  color: string;
}

export default defineComponent({
  name: 'NotificationBell',
  props: {
    autoFetch: {
      type: Boolean,
      default: true
    },
    fetchInterval: {
      type: Number,
      default: 30000
    }
  },
  setup(props) {
    const isOpen = ref(false);
    const loading = ref(false);
    const notifications = ref<Notification[]>([]);
    const unreadCount = ref(0);
    const bellRef = ref<HTMLElement | null>(null);
    let intervalId: number | null = null;

    const fetchNotifications = async () => {
      loading.value = true;
      try {
        const response = await $api.get('/api/notifications', {
          params: { limit: 20 }
        });
        if (response.data.success) {
          notifications.value = response.data.notifications;
          unreadCount.value = response.data.unread_count || 0;
        }
      } catch (error) {
        console.error('Erreur chargement notifications:', error);
      } finally {
        loading.value = false;
      }
    };

    const toggleDropdown = () => {
      isOpen.value = !isOpen.value;
      if (isOpen.value) {
        fetchNotifications();
      }
    };

    const closeDropdown = () => {
      isOpen.value = false;
    };

    const handleNotificationClick = async (notif: Notification) => {
      if (!notif.is_read) {
        await markAsRead(notif);
      }
      
      if (notif.link) {
        closeDropdown();
        window.location.href = notif.link;
      }
    };

    const markAsRead = async (notif: Notification) => {
      try {
        await $api.put(`/api/notifications/${notif.id}/read`);
        notif.is_read = true;
        unreadCount.value = Math.max(0, unreadCount.value - 1);
      } catch (error) {
        console.error('Erreur marquage comme lu:', error);
      }
    };

    const markAllAsRead = async () => {
      try {
        await $api.put('/api/notifications/read-all');
        notifications.value.forEach(n => n.is_read = true);
        unreadCount.value = 0;
      } catch (error) {
        console.error('Erreur marquage tous comme lus:', error);
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (bellRef.value && !bellRef.value.contains(event.target as Node)) {
        closeDropdown();
      }
    };

    const handleNewNotification = (event: CustomEvent) => {
      const newNotif = event.detail;
      notifications.value.unshift(newNotif);
      unreadCount.value += 1;
    };

    onMounted(() => {
      if (props.autoFetch) {
        fetchNotifications();
        intervalId = window.setInterval(fetchNotifications, props.fetchInterval);
      }
      
      document.addEventListener('click', handleClickOutside);
      window.addEventListener('new-notification', handleNewNotification as (event: Event) => void);
    });

    onUnmounted(() => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('new-notification', handleNewNotification as (event: Event) => void);
    });

    return {
      isOpen,
      loading,
      notifications,
      unreadCount,
      bellRef,
      toggleDropdown,
      closeDropdown,
      handleNotificationClick,
      markAsRead,
      markAllAsRead,
      fetchNotifications
    };
  }
});
</script>

<style lang="scss" scoped>
@use '@/styles/variables' as *;

.notification-bell {
  position: relative;
  display: inline-block;
}

.bell-btn {
  position: relative;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0px;
  border-radius: 50%;
  transition: background 0.2s;
  color: #666;
  
  &:hover {
    background: #f5f5f5;
  }
  
  &.has-notifications {
    color: $orange-doux;
  }
  
  .bell-icon {
    font-size: 20px;
  }
  
  .badge {
    position: absolute;
    top: 2px;
    right: 2px;
    background: #dc3545;
    color: white;
    font-size: 10px;
    font-weight: 600;
    min-width: 18px;
    height: 12px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
  }
}

.notifications-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: -8px;
  width: 380px;
  max-width: 90vw;
  max-height: 500px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 1000;
  
  .dropdown-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #eee;
    
    .title {
      font-weight: 600;
      font-size: 16px;
      color: $gris-fonce;
      display: flex;
      align-items: center;
      gap: 8px;
      
      svg {
        color: $orange-doux;
      }
    }
    
    .header-actions {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    
    .mark-all-read {
      background: transparent;
      border: none;
      color: $orange-doux;
      font-size: 12px;
      cursor: pointer;
      
      &:hover {
        text-decoration: underline;
      }
    }
    
    // ✅ BOUTON DE FERMETURE
    .close-dropdown {
      background: transparent;
      border: none;
      color: $gris-moyen;
      cursor: pointer;
      font-size: 16px;
      padding: 4px 8px;
      border-radius: 4px;
      transition: all 0.2s;
      
      &:hover {
        background: #f5f5f5;
        color: $gris-fonce;
      }
    }
  }
  
  .loading-state {
    padding: 40px 20px;
    text-align: center;
    
    .spinner {
      width: 30px;
      height: 30px;
      border: 3px solid #f3f3f3;
      border-top: 3px solid $orange-doux;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto;
    }
  }
  
  .empty-state {
    padding: 40px 20px;
    text-align: center;
    color: $gris-moyen;
    
    svg {
      font-size: 32px;
      color: $gris-moyen;
      margin-bottom: 10px;
    }
  }
  
  .notifications-list {
    max-height: 350px;
    overflow-y: auto;
    
    &::-webkit-scrollbar {
      width: 4px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: $orange-doux;
      border-radius: 2px;
    }
  }
  
  .notification-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 20px;
    cursor: pointer;
    transition: background 0.2s;
    border-bottom: 1px solid #f5f5f5;
    position: relative;
    
    &:hover {
      background: #f8f9fa;
    }
    
    &.unread {
      background: #fff8f0;
      
      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 3px;
        background: $orange-doux;
      }
    }
    
    .notif-icon {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      flex-shrink: 0;
      
      svg {
        font-size: 14px;
      }
    }
    
    .notif-content {
      flex: 1;
      min-width: 0;
      
      .notif-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
        
        .notif-title {
          font-weight: 500;
          font-size: 13px;
          color: $gris-fonce;
        }
        
        .notif-time {
          font-size: 10px;
          color: $gris-moyen;
          white-space: nowrap;
        }
      }
      
      .notif-message {
        font-size: 12px;
        color: $gris-moyen;
        margin: 2px 0 0;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
    }
    
    .notif-mark-read {
      background: transparent;
      border: none;
      color: $gris-moyen;
      cursor: pointer;
      padding: 4px;
      flex-shrink: 0;
      
      &:hover {
        color: $orange-doux;
      }
    }
  }
  
  .dropdown-footer {
    padding: 12px 20px;
    border-top: 1px solid #eee;
    text-align: center;
    
    a {
      color: $orange-doux;
      text-decoration: none;
      font-size: 13px;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 480px) {
  .notifications-dropdown {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    max-width: 100%;
    max-height: 100%;
    border-radius: 0;
    transform: none !important;
    
    .notifications-list {
      max-height: calc(100vh - 150px);
    }
  }
}
</style>